import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Download, ChevronDown, ChevronRight, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const scheduleLabel = (r) => {
  if (r.class_date) {
    const d = new Date(r.class_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${d} • ${r.time}`;
  }
  return `${r.day_of_week} • ${r.time}`;
};

const ParticipantsSummary = () => {
  const [rows, setRows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [level, setLevel] = useState('all');
  const [instructor, setInstructor] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const [canViewRevenue, setCanViewRevenue] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (level && level !== 'all') params.level = level;
      if (instructor && instructor !== 'all') params.instructor = instructor;
      const res = await axios.get(`${API}/admin/class-participants`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(res.data.classes);
      setLevels(res.data.levels);
      setInstructors(res.data.instructors || []);
      setCanViewRevenue(res.data.can_view_revenue !== false);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Failed to load participant summary');
    }
  }, [startDate, endDate, level, instructor]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const grandTotal = rows.reduce((sum, r) => sum + Number(r.total_payout || 0), 0);
  const grandParticipants = rows.reduce((sum, r) => sum + Number(r.confirmed_participants || 0), 0);

  const handleCheckIn = async (participant) => {
    if (!participant.id) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${participant.id}/attendance`, { attended: !participant.attended }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(participant.attended ? `${participant.name} marked absent` : `${participant.name} checked in`);
      fetchData();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update check-in');
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Class', 'Choreographer', 'Schedule', 'Participant', 'Email', 'Phone'];
    if (canViewRevenue) header.push('Amount Paid');
    header.push('Booking Date', 'Source');
    const lines = [header.join(',')];
    rows.forEach((r) => {
      if (r.participants.length === 0) {
        const row = [esc(r.name), esc(r.instructor), esc(scheduleLabel(r)), esc('— no confirmed participants —'), '', ''];
        if (canViewRevenue) row.push('0.00');
        row.push('', '');
        lines.push(row.join(','));
      } else {
        r.participants.forEach((p) => {
          const row = [esc(r.name), esc(r.instructor), esc(scheduleLabel(r)), esc(p.name), esc(p.email), esc(p.phone)];
          if (canViewRevenue) row.push(Number(p.amount || 0).toFixed(2));
          row.push(esc(p.booking_date), esc(p.source));
          lines.push(row.join(','));
        });
      }
      const totalRow = [esc(`${r.name} — TOTAL`), '', '', `${r.confirmed_participants} participant(s)`, '', ''];
      if (canViewRevenue) totalRow.push(Number(r.total_payout || 0).toFixed(2));
      totalRow.push('', '');
      lines.push(totalRow.join(','));
      lines.push('');
    });
    const grandRow = ['GRAND TOTAL', '', '', `${grandParticipants} participant(s)`, '', ''];
    if (canViewRevenue) grandRow.push(grandTotal.toFixed(2));
    grandRow.push('', '');
    lines.push(grandRow.join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Payout summary exported');
  };

  return (
    <div className="p-8" data-testid="participants-summary">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-2">
        <h2 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'var(--heading-font)' }} data-testid="participants-title">
          <Users size={28} /> Class Participants &amp; Payouts
        </h2>
        <button
          onClick={exportCsv}
          className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2"
          data-testid="export-payouts-btn"
        >
          <Download size={18} /> EXPORT CSV
        </button>
      </div>
      <p className="text-zinc-600 mb-6">Confirmed (paid) participants and total payout per class</p>

      {/* Totals */}
      <div className={`grid grid-cols-1 ${canViewRevenue ? 'sm:grid-cols-2' : ''} gap-4 mb-6`}>
        <div className="bg-white border border-zinc-200 p-5" data-testid="grand-total-participants">
          <p className="text-sm text-zinc-500">Total Confirmed Participants</p>
          <p className="text-3xl font-bold">{grandParticipants}</p>
        </div>
        {canViewRevenue && (
          <div className="bg-black text-white p-5" data-testid="grand-total-payout">
            <p className="text-sm text-zinc-300">Total Payout (Confirmed)</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--accent)' }}>{peso(grandTotal)}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6 bg-white border border-zinc-200 p-4">
        <div>
          <Label htmlFor="start-date">From (class date)</Label>
          <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="participants-start-date" />
        </div>
        <div>
          <Label htmlFor="end-date">To (class date)</Label>
          <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="participants-end-date" />
        </div>
        <div className="min-w-[180px]">
          <Label>Class Type / Level</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="participants-level-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              {levels.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Label>Choreographer</Label>
          <Select value={instructor} onValueChange={setInstructor}>
            <SelectTrigger data-testid="participants-instructor-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Choreographers</SelectItem>
              {instructors.map((i) => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(startDate || endDate || level !== 'all' || instructor !== 'all') && (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); setLevel('all'); setInstructor('all'); }}
            className="text-sm underline text-zinc-600"
            data-testid="participants-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-100 border-b border-zinc-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-bold">Class</th>
              <th className="px-6 py-3 text-left text-sm font-bold">Level</th>
              <th className="px-6 py-3 text-left text-sm font-bold">Choreographer</th>
              <th className="px-6 py-3 text-left text-sm font-bold">Schedule</th>
              <th className="px-6 py-3 text-right text-sm font-bold">Confirmed</th>
              <th className="px-6 py-3 text-right text-sm font-bold">Remaining</th>
              {canViewRevenue && <th className="px-6 py-3 text-right text-sm font-bold">Total Payout</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.class_id}>
                <tr
                  className="border-b border-zinc-100 cursor-pointer hover:bg-zinc-50"
                  onClick={() => toggleExpand(r.class_id)}
                  data-testid={`participant-row-${r.class_id}`}
                >
                  <td className="px-6 py-3 font-medium">
                    <span className="inline-flex items-center gap-2">
                      {expanded.has(r.class_id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      {r.name}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-zinc-600">{(r.levels && r.levels.length ? r.levels : [r.level]).filter(Boolean).join(', ')}</td>
                  <td className="px-6 py-3 text-zinc-600" data-testid={`participant-instructor-${r.class_id}`}>{r.instructor || '—'}</td>
                  <td className="px-6 py-3 text-zinc-600">{scheduleLabel(r)}</td>
                  <td className="px-6 py-3 text-right font-bold" data-testid={`participant-confirmed-${r.class_id}`}>{r.confirmed_participants}</td>
                  <td className="px-6 py-3 text-right">
                    {r.slots_remaining === 0 ? (
                      <span className="text-red-600 font-bold">FULL</span>
                    ) : (
                      <span className="text-green-700 font-bold">{r.slots_remaining}</span>
                    )}
                  </td>
                  {canViewRevenue && <td className="px-6 py-3 text-right font-bold" data-testid={`participant-payout-${r.class_id}`}>{peso(r.total_payout)}</td>}
                </tr>
                {expanded.has(r.class_id) && (
                  <tr className="bg-zinc-50" data-testid={`participant-detail-${r.class_id}`}>
                    <td colSpan={canViewRevenue ? 7 : 6} className="px-6 py-4">
                      {r.participants.length === 0 ? (
                        <p className="text-sm text-zinc-500">No confirmed participants yet.</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-zinc-500 border-b border-zinc-200">
                              <th className="py-2 text-left font-semibold">Name</th>
                              <th className="py-2 text-left font-semibold">Email</th>
                              <th className="py-2 text-left font-semibold">Booking Date</th>
                              {canViewRevenue && <th className="py-2 text-right font-semibold">Amount Paid</th>}
                              <th className="py-2 text-right font-semibold">Check-In</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.participants.map((p, i) => (
                              <tr key={i} className="border-b border-zinc-100" data-testid={`participant-detail-row-${r.class_id}-${i}`}>
                                <td className="py-2 font-medium">{p.name} {p.source === 'walk-in' && <span className="text-[10px] text-blue-600 border border-blue-300 rounded px-1 ml-1">walk-in</span>}</td>
                                <td className="py-2 text-zinc-600">{p.email || '—'}</td>
                                <td className="py-2 text-zinc-600">{p.booking_date || '—'}</td>
                                {canViewRevenue && <td className="py-2 text-right font-medium">{peso(p.amount)}</td>}
                                <td className="py-2 text-right">
                                  <button
                                    onClick={() => handleCheckIn(p)}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${p.attended ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                                    data-testid={`participant-checkin-${r.class_id}-${i}`}
                                  >
                                    {p.attended ? <><UserCheck size={14} /> Present</> : <><UserX size={14} /> Check In</>}
                                  </button>
                                </td>
                              </tr>
                            ))}
                            <tr className="font-bold">
                              <td className="py-2" colSpan={canViewRevenue ? 4 : 3}>Total ({r.confirmed_participants} participant{r.confirmed_participants === 1 ? '' : 's'})</td>
                              {canViewRevenue && <td className="py-2 text-right">{peso(r.total_payout)}</td>}
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={canViewRevenue ? 7 : 6} className="px-6 py-8 text-center text-zinc-500" data-testid="participants-empty">No classes found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParticipantsSummary;
